import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { blogAPI, BlogPostSummary, BlogTag } from '../services/blog';
import Logo from '../components/Logo';
import './Blog.css';

const Blog: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTag = searchParams.get('tag');

  const [posts, setPosts] = useState<BlogPostSummary[]>([]);
  const [tags, setTags] = useState<BlogTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Blog — Lomi 🍋';
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      blogAPI.list({ tag: activeTag || undefined, limit: 30 }),
      blogAPI.listTags(),
    ])
      .then(([listRes, tagsRes]) => {
        if (cancelled) return;
        setPosts(listRes.posts);
        setTags(tagsRes);
      })
      .catch(e => {
        if (cancelled) return;
        setError(e.message || 'Failed to load posts');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTag]);

  const formatDate = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="blog-page">
      <header className="blog-header">
        <button onClick={() => navigate('/')} className="blog-back-button" aria-label="Back to home">
          ← Home
        </button>
        <div className="blog-brand" onClick={() => navigate('/')}>
          <Logo height={30} />
        </div>
      </header>

      <main className="blog-content">
        <section className="blog-hero">
          <h1>Field notes, theses, and essays</h1>
          <p className="blog-tagline">
            Notes on education, language, and building for African students.
          </p>
        </section>

        {tags.length > 0 && (
          <nav className="blog-tags-nav" aria-label="Filter by tag">
            <button
              className={`blog-tag-pill ${!activeTag ? 'active' : ''}`}
              onClick={() => setSearchParams({})}
            >
              All
            </button>
            {tags.map(t => (
              <button
                key={t.tag}
                className={`blog-tag-pill ${activeTag === t.tag ? 'active' : ''}`}
                onClick={() => setSearchParams({ tag: t.tag })}
              >
                {t.tag} <span className="blog-tag-count">{t.post_count}</span>
              </button>
            ))}
          </nav>
        )}

        {loading && (
          <div className="blog-status">Loading posts…</div>
        )}

        {error && (
          <div className="blog-status blog-status-error">{error}</div>
        )}

        {!loading && !error && posts.length === 0 && (
          <div className="blog-status">
            {activeTag
              ? `No posts tagged "${activeTag}" yet.`
              : 'No posts yet — first one coming soon.'}
          </div>
        )}

        <div className="blog-post-list">
          {posts.map(post => (
            <article
              key={post.id}
              className="blog-card"
              onClick={() => navigate(`/blog/${post.slug}`)}
            >
              {post.cover_image_url && (
                <div
                  className="blog-card-cover"
                  style={{ backgroundImage: `url(${post.cover_image_url})` }}
                />
              )}
              <div className="blog-card-body">
                <div className="blog-card-meta">
                  {formatDate(post.published_at)} · {post.reading_time_min} min read
                </div>
                <h2 className="blog-card-title">{post.title}</h2>
                {post.excerpt && (
                  <p className="blog-card-excerpt">{post.excerpt}</p>
                )}
                <div className="blog-card-footer">
                  <div className="blog-card-author">
                    {post.author_avatar_url && (
                      <img src={post.author_avatar_url} alt={post.author_name} />
                    )}
                    <span>{post.author_name}</span>
                  </div>
                  <div className="blog-card-tags">
                    {(post.tags || []).slice(0, 3).map(t => (
                      <span key={t} className="blog-card-tag">{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </main>

      <footer className="blog-footer">
        <p><Logo height={22} /> · Built in Ethiopia</p>
      </footer>
    </div>
  );
};

export default Blog;
